import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentCompanionIntroData, useSubmitEnrollmentCompanionIntroData } from '@egw/api'
import { Button, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { riskService } from '@egw/risk-service'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { ExperienceName } from '../../../constants/api/experience-name'

const log = new Logger('Enrollment - Companion Intro')

export const IntroCompanionPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  // Reset enrollment session before initiating application
  useEffect(() => {
    void enrollmentCookieService.resetSession()
    void riskService.updateFlow('enrollment')
  }, [])

  const { data, isLoading, error } = useGetEnrollmentCompanionIntroData({
    initiateApplication: true,
    experimentId: ExperienceName.BROKERAGE_COMPANION,
  })
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentCompanionIntroData()

  const includeCompanionAccount = data?.enrollmentData?.investmentProfile.account.includeCompanionAccount

  // If Companion Account should not be created, redirect to dashboard
  useEffect(() => {
    if (includeCompanionAccount === false) {
      navigate(WEALTH_ROUTES.HOME.path)
    }
  }, [includeCompanionAccount])

  // Protect route
  // useValidateRoute(data?.path) // Disabled

  const handleLinkClick = async (): Promise<void> => {
    try {
      const response = await submit({
        proceedIntro: true,
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  return (
    <EnrollmentLayout title="Companion Intro">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError && includeCompanionAccount ? (
          <>
            <PageHeader title={t('investments.brokerage.companion-intro.title')} />

            <p className="mb-2xs typo-primary-md-semibold text-text-header">
              {t('investments.brokerage.companion-intro.transfer_title')}
            </p>

            <p className="typo-primary-sm-regular text-text-meta">
              {t('investments.brokerage.companion-intro.transfer_description')}
            </p>

            <p className="mt-md mb-2xs typo-primary-md-semibold text-text-header">
              {t('investments.brokerage.companion-intro.assets_title')}
            </p>

            <p className="typo-primary-sm-regular text-text-meta">
              {t('investments.brokerage.companion-intro.assets_description')}
            </p>

            <div className="flex-1 md:hidden" />

            <Button
              type="button"
              className="w-full md:w-min mt-lg"
              testId="enrollment-continue-button"
              isLoading={isSubmitting}
              onClick={() => {
                void handleLinkClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
